import { useTenant } from "hooks/settings";
import { Dayjs } from "dayjs";
import { useMemo } from "react";
import {
  LoadingWindowAssignment,
  HeatSummary,
  LabellerTimeline,
  RailcarSlotMaterialSpan,
  ReviewEvent,
  ReviewPeriodData,
  ReviewSlotRevision,
  useGetReviewDayOverviewQuery,
} from "src/store/api/generatedApi";
import { TimelineExclusions } from "../manualReview/hooks/useTimelineExclusions";
import {
  buildTargetedReviewPeriodRows,
  TargetedReviewPeriodRow,
} from "./targetedReviewRows";
import { useTargetedReviewEvents } from "./useTargetedReviewEvents";

const EMPTY_LABELLER_TIMELINES: LabellerTimeline[] = [];
const EMPTY_HEATS: HeatSummary[] = [];
const EMPTY_LOADING_WINDOWS: LoadingWindowAssignment[] = [];
const EMPTY_REVIEW_EVENTS: ReviewEvent[] = [];
const EMPTY_REVIEW_PERIODS: ReviewPeriodData[] = [];
const EMPTY_RAILCAR_SLOT_MATERIALS: RailcarSlotMaterialSpan[] = [];

export const useTargetedReviewRows = ({
  location,
  day,
  includeVerified,
}: {
  location: string;
  day: Dayjs;
  includeVerified: boolean;
}): {
  periodRows: TargetedReviewPeriodRow[];
  labellerTimelines: LabellerTimeline[];
  heats: HeatSummary[];
  loadingWindows: LoadingWindowAssignment[];
  // The day's full event/period sets, for rendering adjacent-period previews
  // around whichever period is focused — not for editing, which stays
  // scoped to a single focus period.
  dayReviewEvents: ReviewEvent[];
  dayReviewPeriods: ReviewPeriodData[];
  reviewSlotRevisions: ReviewSlotRevision[] | undefined;
  railcarSlotMaterials: RailcarSlotMaterialSpan[];
  exclusions: TimelineExclusions;
  isFetching: boolean;
  isError: boolean;
} => {
  const tenantName = useTenant();

  const {
    resolvedResponse: currentResponse,
    supersededResponse,
    exclusions,
    isError,
    isFetching,
  } = useTargetedReviewEvents({
    tenantName,
    location,
    windowStart: day.startOf("day"),
    windowEnd: day.endOf("day"),
    includeVerified,
    skip: !location,
  });
  const resolvedResponse = currentResponse ?? supersededResponse;

  const overviewQuery = useGetReviewDayOverviewQuery(
    {
      tenantName,
      location,
      date: day.format("YYYY-MM-DD"),
    },
    { skip: !location }
  );

  const periodRows = useMemo(() => {
    if (!resolvedResponse || !overviewQuery.currentData) return [];
    return buildTargetedReviewPeriodRows({
      targetedReviewPeriods: resolvedResponse.targeted_review_periods,
      reviewEvents: resolvedResponse.review_events,
      reviewPeriods: overviewQuery.currentData.review_periods,
      ticks: overviewQuery.currentData.ticks,
    });
  }, [resolvedResponse, overviewQuery.currentData]);

  return {
    periodRows,
    labellerTimelines:
      resolvedResponse?.labeller_timelines ?? EMPTY_LABELLER_TIMELINES,
    heats: overviewQuery.data?.heats ?? EMPTY_HEATS,
    loadingWindows:
      overviewQuery.data?.loading_windows ?? EMPTY_LOADING_WINDOWS,
    dayReviewEvents: resolvedResponse?.review_events ?? EMPTY_REVIEW_EVENTS,
    reviewSlotRevisions: resolvedResponse?.review_slot_revisions,
    dayReviewPeriods:
      overviewQuery.data?.review_periods ?? EMPTY_REVIEW_PERIODS,
    railcarSlotMaterials:
      resolvedResponse?.railcar_slot_materials ?? EMPTY_RAILCAR_SLOT_MATERIALS,
    exclusions,
    isFetching:
      isFetching ||
      overviewQuery.isFetching ||
      (Boolean(location) && !exclusions.resolved),
    isError: isError || overviewQuery.isError,
  };
};
